import { errorMessageFromParsedBody } from "../utils/apiErrorMessage";

interface DeliveryPartner {
  id: number;
  vehicle_type: string;
  vehicle_number: string;
  store_id: number;
  store_name: string;
  is_available: boolean;
  total_deliveries: number;
  average_rating: string;
}

interface User {
  id: number;
  phone: string;
  full_name: string;
  role: string;
  partner: DeliveryPartner;
}

interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
}

export const loginDeliveryPartner = async (phone: string, password: string): Promise<LoginResponse> => {
  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/delivery-login/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ phone, password }),
  });

  const response = await res.json();

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error(errorMessageFromParsedBody(response, "Invalid phone number or password"));
    }
    if (res.status === 403) {
      throw new Error(errorMessageFromParsedBody(response, "Access denied"));
    }
    throw new Error(errorMessageFromParsedBody(response, "Login failed"));
  }

  // Extract the actual data from the nested response structure
  // Response structure: { success: true, message: "...", data: { access, refresh, user } }
  const data = response.data || response;

  // Validate that user has delivery_partner role
  if (data.user?.role !== "delivery_partner") {
    throw new Error("Access denied. Only delivery partners are allowed to login.");
  }

  // Validate that user has a partner profile
  if (!data.user?.partner) {
    throw new Error("Delivery partner profile not found");
  }

  const access = data.access || data.access_token;
  const refresh = data.refresh || data.refresh_token;
  if (!access || !refresh) {
    throw new Error("Login failed: missing tokens");
  }

  return {
    access,
    refresh,
    user: data.user,
  };
};

/**
 * Invalidate current session on server (POST /auth/logout/).
 * Body: `{ refresh }` with the current refresh JWT so it is blacklisted (SimpleJWT + custom list).
 * Header: Authorization Bearer access. Best-effort — local session should still be cleared if this fails.
 */
export const logoutDeliveryPartner = async (): Promise<void> => {
  const access =
    localStorage.getItem("access") || localStorage.getItem("token");
  const refresh = localStorage.getItem("refresh");
  if (!refresh) return;

  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) return;

  try {
    await fetch(`${baseUrl.replace(/\/+$/, "")}/auth/logout/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
      },
      body: JSON.stringify({ refresh }),
    });
  } catch {
    /* Network error — still clear local session */
  }
};

/** Request WhatsApp OTP (same endpoint as customer OTP). */
export const sendAuthOtpForPhone = async (phone: string): Promise<void> => {
  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/send-otp/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
  const response = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(errorMessageFromParsedBody(response, "Failed to send verification code"));
  }
};

export type DeliveryPasswordResetPayload = {
  phone: string;
  otp: string;
  new_password: string;
  confirm_password: string;
};

/** After OTP verification, set new password (delivery_partner accounts only). */
export const resetDeliveryPartnerPassword = async (
  payload: DeliveryPasswordResetPayload
): Promise<void> => {
  const res = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/auth/delivery-password-reset/`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const response = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(errorMessageFromParsedBody(response, "Password reset failed"));
  }
};
